/**
 * Handwritten on purpose: SSE bulk-operation stream. REST bulk ops use
 * api/generated.
 */
import { BulkOperationStatus } from 'api/generated/types';
import {
  type CodedErrorDetailBody,
  isCodedErrorDetailBody,
} from 'utils/apiError';
import { isCanceledRequestError } from 'utils/isCanceledRequestError';
import { fetchSse, readSseStream, SseHttpError } from 'utils/sseStream';
import {
  isEnumValue,
  isRecord,
  isString,
  isStringArray,
} from 'utils/typeGuards';

import { StreamController } from './streamController';

/**
 * Bulk Operation Types
 * Matches backend BulkOperationType enum
 */
export enum BulkOperationType {
  ADD_TO_CAMPAIGN = 'ADD_TO_CAMPAIGN',
  BULK_EMAIL_SEND = 'BULK_EMAIL_SEND',
  PROPAGATE_TEMPLATE = 'PROPAGATE_TEMPLATE',
  REGRADE_CANDIDATES = 'REGRADE_CANDIDATES',
}

/**
 * Progress update for a bulk operation
 */
export interface BulkOperationProgress {
  operation_id: string;
  operation_type: BulkOperationType;
  status: BulkOperationStatus;
  total_count: number;
  processed_count: number;
  success_count: number;
  failed_count: number;
}

/**
 * Event data for item processed
 */
export interface ItemProcessedEventData {
  item_id: string;
  processed_count: number;
  success_count: number;
  total_count?: number;
}

/**
 * Event data for item failed
 */
export interface ItemFailedEventData {
  item_id: string;
  error: string;
  processed_count: number;
  failed_count: number;
}

/**
 * Event data for operation started
 */
export interface OperationStartedEventData extends BulkOperationProgress {}

/**
 * Per-job lookup-variable match warnings surfaced on propagation completion.
 * Each entry lists the `label.column` organization variables whose row did not match that
 * job (the fallback will render instead).
 *
 * The backend stores these under `operation_metadata.warnings` and emits them on
 * the SSE `operation_completed` event (both the early-return and polling-complete
 * paths in `server/bulk_operations/api.py`), which this field carries through.
 */
export interface BulkOperationItemWarning {
  item_id: string;
  warnings: string[];
}

/**
 * Event data for operation completed
 */
export interface OperationCompletedEventData
  extends Omit<BulkOperationProgress, 'operation_type'> {
  /** Redis completion events omit the operation type; DB snapshots include it. */
  operation_type?: BulkOperationType;
  warnings?: BulkOperationItemWarning[];
}

/**
 * Why a bulk-operation stream ended in error. The first five come from the
 * server's `error` event (`server/bulk_operations/stream_errors.py`); the rest
 * are failures this client detects itself.
 */
export enum BulkOperationStreamErrorCode {
  OPERATION_NOT_FOUND = 'OPERATION_NOT_FOUND',
  STREAM_CANCELLED = 'STREAM_CANCELLED',
  STREAM_FAILED = 'STREAM_FAILED',
  OPERATION_CANCELLED = 'OPERATION_CANCELLED',
  OPERATION_FAILED = 'OPERATION_FAILED',
  HTTP_ERROR = 'HTTP_ERROR',
  CONNECTION_FAILED = 'CONNECTION_FAILED',
  ENDED_EARLY = 'ENDED_EARLY',
}

/**
 * A stream error: its `code`, the handler's coded `detail` when it raised one,
 * and `error`, English for logs only (never shown; see bulkOperationErrorText).
 */
export interface BulkOperationStreamError {
  code: BulkOperationStreamErrorCode;
  detail?: CodedErrorDetailBody;
  error: string;
}

/** The server's `error` event data, or null when it is not one. */
const decodeStreamError = (value: unknown): BulkOperationStreamError | null => {
  if (!isRecord(value) || !isString(value.error)) return null;
  return {
    // An older server sends no code; treat that as a failed operation.
    code: isEnumValue(BulkOperationStreamErrorCode, value.code)
      ? value.code
      : BulkOperationStreamErrorCode.OPERATION_FAILED,
    ...(isCodedErrorDetailBody(value.detail) ? { detail: value.detail } : {}),
    error: value.error,
  };
};

/**
 * Callbacks for bulk operation SSE events
 */
export interface BulkOperationCallbacks {
  onOperationStarted?: (data: OperationStartedEventData) => void;
  onItemProcessed?: (data: ItemProcessedEventData) => void;
  onItemFailed?: (data: ItemFailedEventData) => void;
  onOperationCompleted?: (data: OperationCompletedEventData) => void;
  onError?: (error: BulkOperationStreamError) => void;
}

const isCount = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value >= 0;
const isOperationType = (value: unknown): value is BulkOperationType =>
  isEnumValue(BulkOperationType, value);
const isStatus = (value: unknown): value is BulkOperationStatus =>
  isEnumValue(BulkOperationStatus, value);
const hasProgressFields = (value: Record<string, unknown>): boolean =>
  isString(value.operation_id) &&
  isStatus(value.status) &&
  isCount(value.total_count) &&
  isCount(value.processed_count) &&
  isCount(value.success_count) &&
  isCount(value.failed_count);
const isProgress = (value: unknown): value is BulkOperationProgress =>
  isRecord(value) &&
  hasProgressFields(value) &&
  isOperationType(value.operation_type);
const isItemProcessed = (value: unknown): value is ItemProcessedEventData =>
  isRecord(value) &&
  isString(value.item_id) &&
  isCount(value.processed_count) &&
  isCount(value.success_count);
const isItemFailed = (value: unknown): value is ItemFailedEventData =>
  isRecord(value) &&
  isString(value.item_id) &&
  isString(value.error) &&
  isCount(value.processed_count) &&
  isCount(value.failed_count);
const isWarning = (value: unknown): value is BulkOperationItemWarning =>
  isRecord(value) && isString(value.item_id) && isStringArray(value.warnings);
const isOperationCompleted = (
  value: unknown
): value is OperationCompletedEventData =>
  isRecord(value) &&
  hasProgressFields(value) &&
  (value.operation_type === undefined ||
    isOperationType(value.operation_type)) &&
  (value.warnings === undefined ||
    (Array.isArray(value.warnings) && value.warnings.every(isWarning)));

/**
 * Bulk Operation Service
 * Handles SSE streaming for bulk operation progress updates
 */
export class BulkOperationService {
  /**
   * Stream progress updates for a bulk operation
   *
   * @param operationId - The operation ID to track
   * @param callbacks - Event callbacks for progress updates
   * @returns StreamController instance for cancellation
   *
   * @example
   * const controller = bulkOperationService.streamProgress(
   *   'op_123',
   *   {
   *     onOperationStarted: (data) => console.log('Started:', data.total_count),
   *     onItemProcessed: (data) => console.log('Processed:', data.processed_count),
   *     onOperationCompleted: (data) => console.log('Done:', data.success_count),
   *     onError: (error) => console.error('Error:', error.code),
   *   }
   * );
   *
   * // Later, to cancel:
   * controller.cancel();
   */
  streamProgress(
    operationId: string,
    callbacks: BulkOperationCallbacks
  ): StreamController {
    const controller = new StreamController();

    // Start the streaming request
    void (async () => {
      try {
        const response = await fetchSse(
          `/bulk_operations/progress/${operationId}`,
          {
            method: 'GET',
            signal: controller.signal,
          }
        );

        await readSseStream(response, {
          isCancelled: () => controller.cancelled,
          onParseError: (error, raw) => {
            console.error('[bulk-operations] malformed SSE payload:', {
              error,
              raw,
            });
          },
          onMessage: ({ event, data }) => {
            let valid = true;
            switch (event) {
              case 'operation_started':
                if (isProgress(data)) callbacks.onOperationStarted?.(data);
                else valid = false;
                break;
              case 'item_processed':
                if (isItemProcessed(data)) callbacks.onItemProcessed?.(data);
                else valid = false;
                break;
              case 'item_failed':
                if (isItemFailed(data)) callbacks.onItemFailed?.(data);
                else valid = false;
                break;
              case 'operation_completed':
                if (isOperationCompleted(data)) {
                  callbacks.onOperationCompleted?.(data);
                  controller.cancel();
                } else valid = false;
                break;
              case 'error': {
                const streamError = decodeStreamError(data);
                if (streamError) {
                  callbacks.onError?.(streamError);
                  controller.cancel();
                } else valid = false;
                break;
              }
              default:
                return;
            }
            if (!valid)
              console.error(
                `[bulk-operations] invalid ${event} SSE payload:`,
                data
              );
          },
        });
        if (!controller.cancelled) {
          callbacks.onError?.({
            code: BulkOperationStreamErrorCode.ENDED_EARLY,
            error: 'Operation stream ended before completion',
          });
          controller.cancel();
        }
      } catch (error) {
        if (!isCanceledRequestError(error)) {
          callbacks.onError?.({
            code:
              error instanceof SseHttpError
                ? BulkOperationStreamErrorCode.HTTP_ERROR
                : BulkOperationStreamErrorCode.CONNECTION_FAILED,
            error:
              error instanceof Error
                ? error.message
                : 'Connection error occurred',
          });
          if (error instanceof SseHttpError) controller.cancel();
        }
      }
    })();

    return controller;
  }
}

// Export singleton instance
export const bulkOperationService = new BulkOperationService();
